import { MaterialSymbol, withStyles } from 'vopay';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useToast } from '@/providers/Toast.provider';
import { useAlertDialog } from '@/providers/AlertDialog.provider';
import useCategoryLabel from '../useCategoryLabel';
import type { CategoryDescriptor } from '../categories';
import styles from './CategoryEditor.module.scss';

const cn = withStyles(styles);

type CategoryEditorProps = {
  transactionID: string;
  // Current backend classification; '' when the transaction is uncategorized
  classification: string;
  side: 'credit' | 'debit';
  categories: CategoryDescriptor[];
  onUpdateCategory: (transactionID: string, classification: string) => Promise<void>;
};

// Banking-app style category cell: a chip showing the current category (or a
// "Categorize" prompt when unknown) that opens a self-contained picker menu;
// picking saves immediately. Deliberately not a Select — this renders inside
// modal drawers where portal-based dropdowns don't work.
export default function CategoryEditor({
  transactionID,
  classification,
  side,
  categories,
  onUpdateCategory,
}: CategoryEditorProps) {
  const { t } = useTranslation('translations', { keyPrefix: 'Customers.PaymentMethodsTab.DataEnrichment.Drawer' });
  const resolveLabel = useCategoryLabel();
  const toast = useToast();
  const alertDialog = useAlertDialog();

  const containerRef = useRef<HTMLSpanElement>(null);
  // Fixed positioning escapes the table's scroll clipping entirely. Inside the
  // drawer, its slide transform makes it the containing block for fixed
  // elements, so coordinates are computed relative to that container.
  const [menuPlacement, setMenuPlacement] = useState<{ left: number; top?: number; bottom?: number } | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const MENU_MAX_HEIGHT = 250;

  // The nearest transformed/filtered ancestor is what position:fixed resolves
  // against (the sliding drawer); null means the viewport
  function findFixedContainingBlock(element: HTMLElement): HTMLElement | null {
    let node = element.parentElement;

    while (node) {
      const { transform, filter, perspective, willChange } = getComputedStyle(node);

      if (transform !== 'none' || filter !== 'none' || perspective !== 'none' || willChange.includes('transform')) {
        return node;
      }

      node = node.parentElement;
    }

    return null;
  }

  function toggleMenu() {
    if (menuPlacement) {
      setMenuPlacement(null);

      return;
    }

    const anchor = containerRef.current;
    if (!anchor) return;

    const rect = anchor.getBoundingClientRect();
    const containingBlock = findFixedContainingBlock(anchor)?.getBoundingClientRect();
    const blockTop = containingBlock?.top ?? 0;
    const blockLeft = containingBlock?.left ?? 0;
    const blockBottom = containingBlock?.bottom ?? window.innerHeight;

    // Flip above the trigger when there isn't room for the menu below it —
    // this is what keeps it usable in a table with only a couple of rows
    const visibleBottom = containingBlock ? Math.min(containingBlock.bottom, window.innerHeight) : window.innerHeight;
    const opensUpward = visibleBottom - rect.bottom < MENU_MAX_HEIGHT;

    // Coordinates are relative to the fixed menu's containing block (the
    // drawer's transform, or the viewport). Upward opens anchor by `bottom` so
    // the menu grows away from the trigger regardless of its height.
    setMenuPlacement({
      left: rect.left - blockLeft,
      top: opensUpward ? undefined : rect.bottom + 4 - blockTop,
      bottom: opensUpward ? blockBottom - (rect.top - 4) : undefined,
    });
  }

  const selectableCategories = categories.filter((descriptor) => descriptor.side === side);

  const currentCategory = selectableCategories.find((descriptor) => descriptor.classification === classification);
  // Only registry categories are displayed; anything else (including free-text
  // junk from the enrichment worker) renders as needing categorization
  const label = currentCategory ? resolveLabel(currentCategory) : '';

  async function save(nextClassification: string) {
    setMenuPlacement(null);

    if (!nextClassification || nextClassification === classification) return;

    try {
      setIsSaving(true);
      await onUpdateCategory(transactionID, nextClassification);
      toast.success(t('categoryUpdated', { defaultValue: 'Transaction category updated' }));
    } catch (error) {
      console.error(error);

      alertDialog.error(
        t('categoryUpdateFailedTitle', { defaultValue: 'Update failed' }),
        t('categoryUpdateFailed', { defaultValue: 'Could not update the transaction category.' }),
      );
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <span ref={containerRef} className={cn('container')}>
      {label ? (
        <>
          <span className={cn('label')}>{isSaving ? t('categorySaving', { defaultValue: 'Saving…' }) : label}</span>

          <button
            type="button"
            className={cn('editButton')}
            disabled={isSaving}
            aria-label={t('editCategory', { defaultValue: 'Edit category' })}
            onClick={(event) => {
              event.stopPropagation();
              toggleMenu();
            }}
            data-testid={`category-chip-${transactionID}`}
          >
            <MaterialSymbol icon="edit" size="14px" />
          </button>
        </>
      ) : (
        <button
          type="button"
          className={cn('promptChip')}
          disabled={isSaving}
          onClick={(event) => {
            event.stopPropagation();
            toggleMenu();
          }}
          data-testid={`category-chip-${transactionID}`}
        >
          {isSaving ? t('categorySaving', { defaultValue: 'Saving…' }) : t('categorize', { defaultValue: 'Categorize' })}
          <MaterialSymbol icon="edit" size="13px" />
        </button>
      )}

      {menuPlacement && (
        <>
          <span role="presentation" className={cn('backdrop')} onClick={() => setMenuPlacement(null)} />

          <span
            className={cn('menu')}
            style={{ top: menuPlacement.top, bottom: menuPlacement.bottom, left: menuPlacement.left }}
            role="listbox"
            data-testid={`category-menu-${transactionID}`}
          >
            {selectableCategories.map((descriptor) => (
              <button
                key={descriptor.reportType}
                type="button"
                role="option"
                aria-selected={descriptor.classification === classification}
                className={cn('option', descriptor.classification === classification && 'optionSelected')}
                onClick={(event) => {
                  event.stopPropagation();
                  save(descriptor.classification);
                }}
              >
                {resolveLabel(descriptor)}
                {descriptor.classification === classification && <MaterialSymbol icon="check" size="14px" />}
              </button>
            ))}
          </span>
        </>
      )}
    </span>
  );
}
